import { ChangeEvent } from "react";

import { CaseAdmin, Lab } from "pwapi";

import { displayPriority } from "shared/utils/caseUtils";
import { billingTypeEnumToLabel } from "shared/utils/caseUtils";
import { locationOptions } from "shared/utils/usRegions";

import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import MultiSelectColumnInput from "components/customInputs/MultiColumnSelectInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import ClinicFilter from "./ClinicFilter";
import ClinicGroupFilter from "./ClinicGroupFilter";
import SonicPracticeFilter from "./SonicPracticeFilter";
import TCCliaCertificateFilter from "./TCCliaCertificateFilter";

type CaseFiltersProps = {
  accession: string;
  accessioningLab: string;
  type: string;
  status: string;
  source: string;
  expectedBy: string;
  activeAssignmentName: string;
  orderingDoctorName: string;
  patientName: string;
  patientDateOfBirth: string;
  patientClinicClinicGroupName: string;
  patientClinicSonicPracticeName: string;
  patientClinicClinicGroupId?: number;
  patientClinicSonicPracticeId?: number;
  patientClinicName: string;
  patientClinicLocationState: string;
  patientAddressState: string;
  patientInsuranceInsuranceCompanyName: string;
  grossedByCert: string;
  billingType: string;
  priority?: string;
  qaReviewHold: string[];
  billerName?: string;
  clinicNumber?: string;

  labs: Lab[];
  isManuallyAccessioned?: boolean;

  onChange: Function;
  onChangeManuallyAccessioned: Function;
};

export const qaReviewLabels = {
  reviewed: "Reviewed",
  unreviewed: "Unreviewed",
  "on-hold": "On Hold",
};

const CaseFilters = (props: CaseFiltersProps) => {
  return (
    <>
      <TextInput
        id="accession"
        value={props.accession}
        label="Accession"
        onChange={(e: any) => props.onChange("accession", e.target.value)}
        onClear={() => props.onChange("accession", "")}
      />
      <SelectInput
        id="isManuallyAccessioned"
        label="Accession  Method"
        defaultLabel="All Methods..."
        options={[
          { value: "true", label: "Manual" },
          { value: "false", label: "Ordered" },
        ]}
        value={
          props.isManuallyAccessioned === undefined
            ? ""
            : props.isManuallyAccessioned
            ? "true"
            : "false"
        }
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          props.onChangeManuallyAccessioned(
            "isManuallyAccessioned",
            e.target.value === "" ? undefined : e.target.value === "true"
          );
        }}
      />
      <SelectInput
        id="type"
        label="Case Type"
        defaultLabel="All Types..."
        options={[
          CaseAdmin.type.CLINICAL,
          CaseAdmin.type.CONSULT,
          CaseAdmin.type.HISTORICAL,
          CaseAdmin.type.TC_PC,
        ].map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.type}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("type", e.target.value)
        }
      />
      <TextInput
        id="activeAssignmentName"
        value={props.activeAssignmentName}
        label="Active Assignment"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("activeAssignmentName", e.target.value)
        }
        onClear={() => props.onChange("activeAssignmentName", "")}
      />
      <TextInput
        id="orderingDoctorName"
        value={props.orderingDoctorName}
        label="Ordering Provider"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          props.onChange("orderingDoctorName", e.target.value);
        }}
        onClear={() => props.onChange("orderingDoctorName", "")}
      />
      <TextInput
        id="patientName"
        value={props.patientName}
        label="Patient"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientName", e.target.value)
        }
        onClear={() => props.onChange("patientName", "")}
      />
      <CustomInput
        label="Patient Date of Birth"
        id="patientDateOfBirth"
        onClear={() => props.onChange("patientDateOfBirth", "")}
      >
        <DateInput
          value={props.patientDateOfBirth}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            props.onChange("patientDateOfBirth", e.target.value)
          }
          showPickerOnFocus
          placeholder=" "
        />
      </CustomInput>
      <SelectInput
        id="patientState"
        label="Patient State"
        defaultLabel="All States"
        options={locationOptions}
        value={props.patientAddressState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientAddressState", e.target.value)
        }
      />
      <TextInput
        id="patientInsuranceInsuranceCompanyName"
        value={props.patientInsuranceInsuranceCompanyName}
        label="Patient Insurance Name"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientInsuranceInsuranceCompanyName", e.target.value)
        }
        onClear={() =>
          props.onChange("patientInsuranceInsuranceCompanyName", "")
        }
      />
      <ClinicGroupFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("patientClinicClinicGroupName", newValue.value);
        }}
        value={props.patientClinicClinicGroupName}
      />
      <SonicPracticeFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("patientClinicSonicPracticeName", newValue.value);
        }}
        value={props.patientClinicSonicPracticeName}
      />
      <ClinicFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("patientClinicName", newValue.value);
        }}
        value={props.patientClinicName}
      />
      <SelectInput
        id="patientClinicLocationState"
        label="Clinic State"
        defaultLabel="All States"
        options={locationOptions}
        value={props.patientClinicLocationState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientClinicLocationState", e.target.value)
        }
      />
      <SelectInput
        id="accessioningLab"
        label="Accessioning Lab"
        defaultLabel="All Labs"
        value={props.accessioningLab}
        options={
          props.labs?.map((v) => ({ label: v.name, value: `${v.id}` })) || []
        }
        onChange={(e: any) =>
          props.onChange("accessioningLab", e.target.value || "")
        }
      />
      <SelectInput
        id="status"
        label="Status"
        defaultLabel="All Statuses"
        options={Object.values(CaseAdmin.status).map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.status}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("status", e.target.value)
        }
      />
      <SelectInput
        id="priority"
        label="Case Priority"
        defaultLabel="All Priorities"
        options={Object.values(CaseAdmin.priority).map((v) => ({
          label: displayPriority(v),
          value: v,
        }))}
        value={props.priority || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("priority", e.target.value)
        }
      />
      <SelectInput
        id="source"
        label="Source"
        defaultLabel="All Sources"
        options={Object.values(CaseAdmin.source)
          .filter((value) => value !== CaseAdmin.source.APL)
          .map((v) => ({
            label: v,
            value: v,
          }))}
        value={props.source}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("source", e.target.value)
        }
      />
      <TCCliaCertificateFilter
        onChange={({ value }: { value: string }) => {
          props.onChange("grossedByCert", value);
        }}
        value={props.grossedByCert}
      />
      <SelectInput
        id="billingType"
        label="Billing Type"
        defaultLabel="All Billing Types"
        options={Object.values(CaseAdmin.billing_type).map((v) => ({
          label: billingTypeEnumToLabel(v),
          value: v,
        }))}
        value={props.billingType}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("billingType", e.target.value)
        }
      />
      <MultiSelectColumnInput
        id="qaReviewHold"
        label="QA Review"
        options={[
          { label: qaReviewLabels.reviewed, value: "reviewed" },
          { label: qaReviewLabels.unreviewed, value: "unreviewed" },
          { label: qaReviewLabels["on-hold"], value: "on-hold" },
        ]}
        value={props.qaReviewHold}
        onChange={(newValue: any) => props.onChange("qaReviewHold", newValue)}
      />
      <SelectInput
        id="billerName"
        label="Biller Name"
        defaultLabel=""
        options={["Telcor", "Optum"].map((b) => ({ label: b, value: b }))}
        value={props.billerName || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("billerName", e.target.value)
        }
      />
      <SelectInput
        id="clinicNumber"
        label="Clinic Number"
        defaultLabel=""
        options={["P-Number", "Other"].map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.clinicNumber || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("clinicNumber", e.target.value)
        }
      />
    </>
  );
};

export default CaseFilters;
